import { PING_AUTH_URL, PING_MOBILE_CLIENT_SECRET, PING_MOBILE_CLIENT_ID } from '@/config'
import { auth, fetchConfig } from '@/dataImplementation/auth'
import { isNativeApp } from '@red-ui/bridge'
import { setAuthTokens } from '@vrw/data/src/redux/auth/getAuthTokens'
import { logger } from '@vrw/data/src/utils'
import { UserManagerSettings, WebStorageStateStore } from 'oidc-client-ts'

export const postAuthCallback = async () => {
  try {
    const result = await auth.signinCallback()
    if (!result) {
      throw new Error('No result from auth.signinCallback')
    }
    if (!result.access_token) {
      throw new Error('No access token or id token from auth.signinCallback')
    }
    if (!result.id_token) {
      throw new Error('No id token from auth.signinCallback')
    }

    await setAuthTokens(result.access_token, result.id_token)
    return result
  } catch (error) {
    return logger.warn('oidcClient:postAuthCallback() failed: ', error)
  }
}

export const getHybridAuthConfig = (webConfig: UserManagerSettings): UserManagerSettings => {
  if (!isNativeApp()) {
    return webConfig
  }

  const redBasePath = `${window.location.protocol}//${window.location.host}/virgin-red`

  return {
    automaticSilentRenew: false,
    authority: PING_AUTH_URL,
    redirect_uri: `${redBasePath}/login-success`,
    response_type: 'code',
    scope: 'openid profile email execute_api:user red_api offline_access',
    metadata: {
      authorization_endpoint: `${PING_AUTH_URL}/authorize`,
      token_endpoint: `${PING_AUTH_URL}/token`,
      end_session_endpoint: `${PING_AUTH_URL}/signoff`,
      revocation_endpoint: `${PING_AUTH_URL}/revoke`,
    },
    // library defaults to session storage which can not be shared across tabs/multiple browser sessions
    // we alway requires it to be local (Note: we will likely need to clean up our existing storage of auth)
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    client_id: PING_MOBILE_CLIENT_ID,
    client_secret: PING_MOBILE_CLIENT_SECRET,
    client_authentication: 'client_secret_basic',
    stateStore: new WebStorageStateStore({ store: window.localStorage }),
  }
}

interface PKCEValues {
  code_verifier: string
  code_challenge: string
}
export const setHybridAppState = async (state: string, pkce?: PKCEValues) => {
  try {
    const currentConfig = fetchConfig()
    const basePath = `${window.location.protocol}//${window.location.host}/virgin-red/auth-callback`

    await auth.settings.stateStore.set(
      state,
      JSON.stringify({
        id: state,
        client_id: currentConfig.client_id,
        client_secret: currentConfig.client_secret,
        created: Math.floor(Date.now() / 1000),
        request_type: 'si:r',
        authority: currentConfig.authority,
        scope: currentConfig.scope,
        code_verifier: pkce?.code_verifier ?? true,
        code_challenge: pkce?.code_challenge,
        redirect_uri: `${basePath}?code_verifier=${pkce?.code_verifier}&code_challenge=${pkce?.code_challenge}&code_challenge_method=S256`,
      })
    )
  } catch (error) {
    logger.warn('oidcClient:setAppState() failed: ', error)
  }
}
